import React from 'react';
import { BrowserRouter as Router, Routes, Route, Navigate } from 'react-router-dom';
import { Restaurants } from './pages/Restaurants';
import { RestaurantDetails } from './pages/RestaurantDetails';
import { RestaurantMenu } from './pages/RestaurantMenu';
import { Groceries } from './pages/Groceries';
import { GroceryDetails } from './pages/GroceryDetails';
import { GroceryMenu } from './pages/GroceryMenu';
import { Settings } from './pages/Settings';
import { UserDetails } from './pages/UserDetails';
import { Dashboard } from './pages/Dashboard';
import { PromoCodes } from './pages/PromoCodes';
import { PromoCodeDetails } from './pages/PromoCodeDetails';
import { Banners } from './pages/Banners';
import { BannerCampaignDetails } from './pages/BannerCampaignDetails';
import { PUSH_CAMPAIGN_EDIT_ROLES, PUSH_CAMPAIGN_VIEW_ROLES } from './utils/pushCampaignPermissions';
import { PushCampaigns } from './pages/PushCampaigns';
import { PushCampaignWizard } from './pages/PushCampaignWizard';
import { PushCampaignDetails } from './pages/PushCampaignDetails';
import { Orders } from './pages/Orders';
import { Feedback } from './pages/Feedback';
import { Billing } from './pages/Billing';
import { Resorts } from './pages/Resorts';
import { ResortDetails } from './pages/ResortDetails';
import { FoodTags } from './pages/FoodTags';
import { Accommodations } from './pages/Accommodations';
import { PartnerOnboarding } from './pages/PartnerOnboarding';
import { Layout } from './components/layout/Layout';
import { Login } from './pages/Login';
import { SetPassword } from './pages/SetPassword';
import { ConfirmEmail } from './pages/ConfirmEmail';
import { AuthProvider, useAuth } from './context/AuthContext';
import { DeployOverlay } from './components/DeployOverlay';

// Protected route component that checks user role
const ProtectedRoute = ({
  element,
  allowedRoles
}: {
  element: React.ReactElement;
  allowedRoles?: string[];
}) => {
  const {
    user,
    isAuthenticated
  } = useAuth();
  if (!isAuthenticated) {
    console.log('Not authenticated, redirecting to login'); // Debug log
    return <Navigate to="/login" />;
  }
  if (allowedRoles && user && !allowedRoles.includes(user.role)) {
    console.log('User role not allowed:', user.role); // Debug log
    return <Navigate to={user.role === 'Support' ? '/orders' : '/restaurants'} />;
  }
  return element;
};

const DefaultRedirect = () => {
  const { user } = useAuth();
  if (user?.role === 'Support') {
    return <Navigate to="/orders" replace />;
  }
  return <Navigate to="/restaurants" replace />;
};

export function App() {
  return (
    <AuthProvider>
      <Router future={{ v7_startTransition: true, v7_relativeSplatPath: true }}>
        <Routes>
          <Route path="/login" element={<Login />} />
          <Route path="/set-password" element={<SetPassword />} />
          <Route path="/confirm-email" element={<ConfirmEmail />} />
          <Route
            path="/"
            element={
              <ProtectedRoute
                element={<Layout />}
                allowedRoles={['Resort Super Admin', 'Supervisor', 'Editor', 'Partner Admin', 'Support']}
              />
            }
          >
            <Route index element={<DefaultRedirect />} />
            <Route path="restaurants" element={<Restaurants />} />
            <Route
              path="restaurants/:name"
              element={
                <ProtectedRoute
                  element={<RestaurantDetails />}
                  allowedRoles={['Resort Super Admin', 'Supervisor', 'Editor', 'Partner Admin']}
                />
              }
            />
            <Route
              path="restaurants/:name/menu"
              element={
                <ProtectedRoute
                  element={<RestaurantMenu />}
                  allowedRoles={['Resort Super Admin', 'Supervisor', 'Editor', 'Partner Admin', 'Restaurant Admin', 'Restaurant and Grocery Admin']}
                />
              }
            />
            <Route path="groceries" element={<Groceries />} />
            <Route
              path="groceries/:name"
              element={
                <ProtectedRoute
                  element={<GroceryDetails />}
                  allowedRoles={['Resort Super Admin', 'Supervisor', 'Editor', 'Partner Admin']}
                />
              }
            />
            <Route
              path="groceries/:name/menu"
              element={
                <ProtectedRoute
                  element={<GroceryMenu />}
                  allowedRoles={['Resort Super Admin', 'Supervisor', 'Editor', 'Partner Admin', 'Grocery Admin', 'Restaurant and Grocery Admin']}
                />
              }
            />
            <Route
              path="settings"
              element={
                <ProtectedRoute
                  element={<Settings />}
                  allowedRoles={['Resort Super Admin', 'Supervisor', 'Editor', 'Partner Admin', 'Support']}
                />
              }
            />
            <Route
              path="settings/users/:name"
              element={
                <ProtectedRoute
                  element={<UserDetails />}
                  allowedRoles={['Resort Super Admin', 'Supervisor']}
                />
              }
            />
            <Route path="dashboard" element={<Dashboard />} />
            <Route
              path="billing"
              element={
                <ProtectedRoute
                  element={<Billing />}
                  allowedRoles={['Resort Super Admin', 'Supervisor', 'Partner Admin']}
                />
              }
            />
            <Route
              path="orders"
              element={
                <ProtectedRoute
                  element={<Orders />}
                  allowedRoles={['Resort Super Admin', 'Supervisor', 'Partner Admin', 'Support']}
                />
              }
            />
            <Route
              path="feedback"
              element={
                <ProtectedRoute
                  element={<Feedback />}
                  allowedRoles={['Resort Super Admin', 'Supervisor', 'Partner Admin', 'Support']}
                />
              }
            />
            <Route
              path="promo-codes"
              element={
                <ProtectedRoute
                  element={<PromoCodes />}
                  allowedRoles={['Resort Super Admin', 'Supervisor']}
                />
              }
            />
            <Route
              path="promo-codes/:name"
              element={
                <ProtectedRoute
                  element={<PromoCodeDetails />}
                  allowedRoles={['Resort Super Admin', 'Supervisor']}
                />
              }
            />
            <Route
              path="banners"
              element={
                <ProtectedRoute
                  element={<Banners />}
                  allowedRoles={['Resort Super Admin', 'Supervisor']}
                />
              }
            />
            <Route
              path="banners/new"
              element={
                <ProtectedRoute
                  element={<BannerCampaignDetails />}
                  allowedRoles={['Resort Super Admin', 'Supervisor']}
                />
              }
            />
            <Route
              path="banners/:name"
              element={
                <ProtectedRoute
                  element={<BannerCampaignDetails />}
                  allowedRoles={['Resort Super Admin', 'Supervisor']}
                />
              }
            />
            <Route
              path="push-campaigns"
              element={
                <ProtectedRoute
                  element={<PushCampaigns />}
                  allowedRoles={PUSH_CAMPAIGN_VIEW_ROLES}
                />
              }
            />
            <Route
              path="push-campaigns/new"
              element={
                <ProtectedRoute
                  element={<PushCampaignWizard />}
                  allowedRoles={PUSH_CAMPAIGN_EDIT_ROLES}
                />
              }
            />
            <Route
              path="push-campaigns/:name/edit"
              element={
                <ProtectedRoute
                  element={<PushCampaignWizard />}
                  allowedRoles={PUSH_CAMPAIGN_EDIT_ROLES}
                />
              }
            />
            <Route
              path="push-campaigns/:name"
              element={
                <ProtectedRoute
                  element={<PushCampaignDetails />}
                  allowedRoles={PUSH_CAMPAIGN_VIEW_ROLES}
                />
              }
            />
            <Route
              path="resorts"
              element={
                <ProtectedRoute
                  element={<Resorts />}
                  allowedRoles={['Resort Super Admin', 'Supervisor']}
                />
              }
            />
            <Route
              path="resorts/:name"
              element={
                <ProtectedRoute
                  element={<ResortDetails />}
                  allowedRoles={['Resort Super Admin', 'Supervisor']}
                />
              }
            />
            <Route
              path="partner-onboarding"
              element={
                <ProtectedRoute
                  element={<PartnerOnboarding />}
                  allowedRoles={['Resort Super Admin', 'Supervisor', 'Partner Admin']}
                />
              }
            />
            <Route
              path="food-tags"
              element={
                <ProtectedRoute
                  element={<FoodTags />}
                  allowedRoles={['Resort Super Admin', 'Supervisor', 'Editor']}
                />
              }
            />
            <Route
              path="grocery-food-tags"
              element={<Navigate to="/food-tags" replace />}
            />
            <Route
              path="accommodations"
              element={
                <ProtectedRoute
                  element={<Accommodations />}
                  allowedRoles={['Resort Super Admin', 'Supervisor', 'Editor']}
                />
              }
            />
          </Route>
          {/* Catch all route */}
          <Route path="*" element={<Navigate to="/login" replace />} />
        </Routes>
      </Router>
      <DeployOverlay />
    </AuthProvider>
  );
}
